import { computed, inject, Injectable, signal } from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { QuangAuthService } from "quang/auth";
import { distinctUntilChanged } from "rxjs";

import { RoleDTO, UserInfoResponseDTO, UsersService } from "./apiModule";
import { Roles } from "./core/counter-store/Roles";

@Injectable({
    providedIn: "root",
})

export class UserService {
    private readonly authService = inject(QuangAuthService)
    private readonly usersService = inject(UsersService)

    user = signal<UserInfoResponseDTO | null>(null)
    userRoles = signal<string[]>([])
    isAuthenticated = signal(false)

    /** Full role objects (code + description) as returned by the backend */
    readonly roleDetails = computed<RoleDTO[]>(() => this.user()?.roles ?? [])

    private readonly isUserAuthenticated$ = toObservable(this.authService.isAuthenticated).pipe(distinctUntilChanged(), takeUntilDestroyed()).subscribe((isAuthenticated) => {
        if (!isAuthenticated) {
            this.reset()
        } else {
            this.loadUserInfo()
        }
    })

    /** Loads the logged user detail from the backend (no longer read from the keycloak token claims) */
    loadUserInfo(): void {
        this.usersService.userinfo().subscribe({
            next: (userInfo) => {
                this.setUser(userInfo)
                this.setUserRoles(this.extractRoleCodes(userInfo))
                this.setIsAuthenticated(true)
            },
            error: (error) => {
                console.error("Unable to load the logged user info", error)
                this.reset()
            },
        })
    }

    setUser(user: UserInfoResponseDTO | null): void {
        this.user.set(user)
    }

    setUserRoles(roles: string[]): void {
        this.authService.removeRoles(Array.from(this.authService.roles()))
        this.authService.addRoles(roles)
        this.userRoles.set(roles)
    }

    setIsAuthenticated(isAuthenticated: boolean): void {
        this.isAuthenticated.set(isAuthenticated)
    }

    private reset(): void {
        this.setUser(null)
        this.setUserRoles([])
        this.setIsAuthenticated(false)
    }

    /**
     * Normalizes the backend role codes to the `Roles` values used across the app,
     * so both `pf_administrator` and `ADMINISTRATOR`-like codes are accepted.
     */
    private extractRoleCodes(userInfo: UserInfoResponseDTO | null): string[] {
        return (userInfo?.roles ?? [])
            .map((role) => this.normalizeRoleCode(role.code))
            .filter((code): code is string => !!code)
    }

    private normalizeRoleCode(code?: string): string | null {
        if (!code?.length) {
            return null
        }

        const canonical = this.canonicalRole(code)
        const matched = Object.values(Roles).find((role) => this.canonicalRole(role) === canonical)

        if (!matched) {
            console.warn(`Unmapped role code received from the backend: ${code}`)
            return code
        }

        return matched
    }

    /** `pf_servicedesk`, `SERVICE_DESK`, `Service Desk` all collapse to `servicedesk` */
    private canonicalRole(code: string): string {
        return code.trim().toLowerCase().replace(/[^a-z0-9]/g, "").replace(/^pf/, "")
    }
}
